export interface SpeakerData {
  id: string
  speakerName: string
  speakerEmail: string
  title: string
  organization: string
  description: string
  speakerImage: string
  linkedinUrl: string
  facebookUrl: string
  twitterUrl: string
  websiteUrl: string
}

export interface PollOption {
  id: string
  text: string
}

export interface PollData {
  id: string
  question: string
  questionType: "single" | "multiple"
  options: PollOption[]
}

export interface SurveyQuestion {
  id: string
  question: string
  questionType: "short" | "singleChoice" | "multipleChoice" | "star"
  options: string
}

export interface SurveyData {
  title: string
  description: string
  questions: SurveyQuestion[]
}

export interface WebinarFormData {
  // Step 0 – Create Webinar
  title: string
  description: string
  date: Date | undefined
  time: string
  durationHrs: number
  durationMins: number
  timezone: string
  hostName: string
  hostEmail: string
  invitees: string[]
  maxRegistrants: number
  passwordEnabled: boolean
  password: string
  recording: boolean
  anyTimeJoin: boolean
  recurring: boolean
  additionalTimezones: string[]

  // Step 1 – Registration
  withRegistration: boolean
  registrationFields: Array<{ field: string; required: boolean }>
  domainRestrictionEnabled: boolean
  domainRestrictionType: 1 | 2
  authorizedDomains: string[]
  restrictedDomains: string[]
  automaticMailApproval: boolean
  registrationLimit: number
  bannerUrl: string

  // Step 2 – Notifications
  reminders: {
    firstReminder: { enabled: boolean; time: number }
    secondReminder: { enabled: boolean; time: number }
    thirdReminder: { enabled: boolean; time: number }
  }
  postWebinar: {
    thankYouEmail: { enabled: boolean; time: number }
    followUpEmail: { enabled: boolean; time: number }
  }

  // Step 3 – Branding
  webinarTheme: { primary: string; navBar: string }
  webinarLogo: string
  webinarBackground: string
  webinarIntroVideo: string

  // Step 4 – Speakers
  speakers: SpeakerData[]

  // Step 5 – Polling
  polls: PollData[]

  // Step 6 – Surveys
  survey: SurveyData | null

  // Step 7 – Engagement
  chat: boolean
  chatVisibility: "host" | "attendees"
  liveQandA: boolean
  reactions: boolean
  pollsEnabled: boolean
  resources: boolean
  surveysEnabled: boolean
}

export const defaultWebinarData: WebinarFormData = {
  title: "",
  description: "",
  date: new Date(),
  time: "09:00",
  durationHrs: 0,
  durationMins: 30,
  timezone: "Asia/Kolkata",
  hostName: "",
  hostEmail: "",
  invitees: [],
  maxRegistrants: 100,
  passwordEnabled: false,
  password: "",
  recording: true,
  anyTimeJoin: false,
  recurring: false,
  additionalTimezones: [],

  withRegistration: false,
  registrationFields: [
    { field: "Full Name", required: true },
    { field: "Email", required: true },
  ],
  domainRestrictionEnabled: false,
  domainRestrictionType: 1,
  authorizedDomains: [],
  restrictedDomains: [],
  automaticMailApproval: true,
  registrationLimit: 100,
  bannerUrl: "",

  reminders: {
    firstReminder: { enabled: false, time: 0 },
    secondReminder: { enabled: false, time: 0 },
    thirdReminder: { enabled: false, time: 0 },
  },
  postWebinar: {
    thankYouEmail: { enabled: false, time: 2 },
    followUpEmail: { enabled: false, time: 2 },
  },

  webinarTheme: { primary: "#042531", navBar: "#043040" },
  webinarLogo: "",
  webinarBackground: "",
  webinarIntroVideo: "",

  speakers: [],
  polls: [],
  survey: null,

  chat: true,
  chatVisibility: "host",
  liveQandA: false,
  reactions: true,
  pollsEnabled: false,
  resources: false,
  surveysEnabled: false,
}

export interface StepProps {
  data: WebinarFormData
  onChange: (patch: Partial<WebinarFormData>) => void
  errors?: Record<string, string>
  clearError?: (key: string) => void
}
